/** The application shell: context bar, navigation, routed view, command drawer, stream (U001-U005). */
import { HEALTH_PATH, LOGOUT_PATH } from "../contracts/constants.ts";
import { decodeCommandRow, type CommandRow } from "../contracts/commands.ts";
import { decodeProjectionHealth, type Capabilities } from "../contracts/envelope.ts";
import { decodeAccountRow, decodeTodayRow, type AccountRow } from "../contracts/views.ts";
import { clearDetailCache } from "../views/detail/index.ts";
import { resolveView } from "../views/index.ts";
import { mountNotFound } from "../views/stubs.ts";
import { entryBlock, reduceToday } from "../views/today/model.ts";
import { ApiClient, type FetchLike } from "./client.ts";
import { createCommandPort, type CommandServices } from "./command-port.ts";
import { createCommandDrawer } from "./command-drawer.ts";
import { IntentKeys, type ChannelLike } from "./intent-keys.ts";
import { createContextBar, type ContextInfo } from "./context-bar.ts";
import { Scope, h, setChildren } from "./dom.ts";
import { clearSensitiveState, createAcknowledgements, installPageHide, logout } from "./latches.ts";
import { AREAS, AREA_LABELS, hashOf, startRouter, type Parsed } from "./router.ts";
import { createRum, listenForErrors, pageLoadMs } from "./rum.ts";
import { renderStateBanner, stateOfError } from "./state.ts";
import { createStore } from "./store.ts";
import {
  StreamClient,
  StreamRegistry,
  type ConnectionState,
  type EventSourceFactory,
  type Subscription,
} from "./stream.ts";
import { detectSupport, liveEnvironment, type Support } from "./support.ts";
import type { Mono } from "./time.ts";
import type { ViewHandle } from "./view.ts";

export interface ShellDeps {
  readonly win: Window;
  readonly fetch: FetchLike;
  readonly createSource: EventSourceFactory;
  readonly mono: Mono;
  readonly navigate: (url: string) => void;
  readonly schedule: (task: () => void, ms: number) => () => void;
}

export interface Shell {
  /** Resolves once the first account scope (context, commands, stream) has loaded. */
  readonly ready: Promise<void>;
  dispose(): void;
}

const COMMANDS_MAX = 200;
const POLL_MS = 1500;

/** The cross-tab channel for intent identities, or `null` where the browser has none that works. */
function openChannel(win: Window): ChannelLike | null {
  try {
    const Channel = (win as unknown as { BroadcastChannel: new (name: string) => ChannelLike })
      .BroadcastChannel;
    const channel = new Channel("ssi-intents");
    return typeof channel.postMessage === "function" ? channel : null;
  } catch {
    return null;
  }
}
const OFF: Capabilities = { rum: false, routingEnabled: false };

/** Boot the application into `root`. */
export function bootShell(root: HTMLElement, deps: ShellDeps): Shell {
  const { win } = deps;
  const scope = new Scope();
  const support: Support = detectSupport(liveEnvironment(win));
  const registry = new StreamRegistry();
  const acknowledgements = createAcknowledgements();
  const streamSequence = createStore<number | null>(null);
  const commandsEnabled = createStore(false);
  const commands = new Map<string, { row: CommandRow; version: number }>();
  const drawer = createCommandDrawer();
  let capabilities = OFF;
  let info: ContextInfo = {
    environment: null,
    mode: null,
    accountId: null,
    accounts: [],
    exchangeDate: null,
    strategyVersion: null,
    connection: { kind: "idle" },
    freshness: null,
    ageUs: null,
    lastSync: null,
    entryBlocks: [],
    support,
  };
  let stream: StreamClient | null = null;
  let view: ViewHandle | null = null;
  let lastParsed: Parsed = {
    kind: "route",
    route: { area: "today", sub: null, param: null, query: {} },
  };
  let signedOut = false;
  let bufferPeak = 0;
  let routed = false;
  let booted = false;

  const client = new ApiClient({
    fetch: deps.fetch,
    onUnauthorised: () => {
      void expire();
    },
  });

  const keys = new IntentKeys({
    storage: win.sessionStorage,
    channel: openChannel(win),
    mint: () => win.crypto.randomUUID(),
  });
  const commandServices: CommandServices = {
    port: createCommandPort(client),
    keys,
    pollMs: POLL_MS,
  };

  const main = h("main", { attrs: { id: "main", tabindex: "-1" } });
  const nav = h(
    "nav",
    { attrs: { "aria-label": "Areas" } },
    h(
      "ul",
      {},
      ...AREAS.map((area) =>
        h("li", {}, h("a", { attrs: { href: hashOf(area) } }, AREA_LABELS[area])),
      ),
    ),
  );

  const recompute = (): void => {
    commandsEnabled.set(
      support.supported &&
        info.connection.kind === "live" &&
        info.freshness === "FRESH" &&
        !signedOut,
    );
    win.document.body.dataset.commandsEnabled = String(commandsEnabled.get());
  };
  const bar = createContextBar({
    onAccount: (accountId) => {
      void switchAccount(accountId);
    },
    onLogout: () => {
      void logout({
        state: sensitive(),
        endSession: () => client.post(LOGOUT_PATH, {}, () => undefined),
        navigate: () => {
          client.forget();
          signedOut = true;
          recompute();
          deps.navigate("/");
        },
      });
    },
  });
  const change = (patch: Partial<ContextInfo>): void => {
    info = { ...info, ...patch };
    bar.update(info);
    recompute();
  };

  const clearCommands = (): void => {
    commands.clear();
    drawer.setRows([]);
  };
  const clearDisplay = (): void => {
    view?.dispose();
    view = null;
    setChildren(main, []);
  };
  function sensitive(): Parameters<typeof clearSensitiveState>[0] {
    return {
      closeStreams: () => {
        stream?.dispose();
        stream = null;
      },
      abortRequests: () => client.newGeneration(),
      clearStores: [
        clearCommands,
        () => {
          keys.clear();
        },
        () => {
          acknowledgements.set(new Set());
        },
        clearDetailCache,
        clearDisplay,
        () => {
          streamSequence.set(null);
        },
      ],
      storage: win.sessionStorage,
    };
  }

  async function expire(): Promise<void> {
    if (signedOut) {
      return;
    }
    signedOut = true;
    clearSensitiveState(sensitive());
    change({ connection: { kind: "idle" } });
    setChildren(main, [renderStateBanner({ kind: "unauthorised", status: 401 })]);
    await Promise.resolve();
  }

  const showCommands = (): void => {
    const rows = [...commands.values()]
      .map((entry) => entry.row)
      .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
      .slice(0, COMMANDS_MAX);
    drawer.setRows(rows);
  };

  const snapshot = async (): Promise<readonly Subscription[] | null> => {
    const account = info.accountId;
    const answer = await client.read("commands", decodeCommandRow, { accountId: account });
    if (answer === null || account === null) {
      return null;
    }
    commands.clear();
    for (const row of answer.page.rows) {
      const entity = `command.${row.commandId}`;
      commands.set(entity, { row, version: answer.entityVersions.get(entity) ?? 0 });
    }
    showCommands();
    streamSequence.set(answer.watermark);
    change({
      freshness: answer.freshness,
      ageUs: answer.ageUs,
      lastSync: answer.snapshotTime,
      mode: answer.mode,
    });
    return [
      {
        projection: "commands",
        accountId: account,
        schemaVersion: answer.schemaVersion,
        snapshotId: answer.snapshotId,
        afterWatermark: answer.watermark,
        coalescing: false,
      },
    ];
  };

  const startStream = (): void => {
    stream = new StreamClient({
      createSource: deps.createSource,
      registry,
      generation: () => client.generation,
      snapshot,
      schedule: deps.schedule,
      onState: (connection: ConnectionState) => {
        change({ connection });
      },
      onBuffered: (events) => {
        bufferPeak = Math.max(bufferPeak, events);
        win.document.body.dataset.streamBufferPeak = String(bufferPeak);
      },
      onEvent: (event) => {
        streamSequence.set(event.sequence);
        let row: CommandRow;
        try {
          row = decodeCommandRow(event.data);
        } catch {
          stream?.resync("BAD_FRAME");
          return;
        }
        const known = commands.get(event.entityId);
        if (known === undefined || event.version > known.version) {
          commands.set(event.entityId, { row, version: event.version });
          showCommands();
        }
      },
    });
    void snapshot().then((subscriptions) => {
      if (subscriptions !== null) {
        stream?.connect(subscriptions);
      }
    });
  };

  const loadContext = async (): Promise<void> => {
    const answer = await client.read("today", decodeTodayRow, { accountId: info.accountId });
    if (answer === null) {
      return;
    }
    const model = reduceToday(answer.page.rows);
    change({
      environment: answer.environment,
      mode: answer.mode,
      exchangeDate: model.strategy?.exchangeDate ?? null,
      strategyVersion: model.strategy?.version ?? null,
      entryBlocks: entryBlock(model).reasons,
    });
  };

  const mount = (parsed: Parsed): void => {
    lastParsed = parsed;
    view?.dispose();
    view = null;
    if (!booted || signedOut) {
      return;
    }
    const viewScope = new Scope();
    const route =
      parsed.kind === "route"
        ? parsed.route
        : { area: "today" as const, sub: null, param: null, query: {} };
    const factory = parsed.kind === "route" ? resolveView(parsed.route) : mountNotFound;
    view = factory({
      client,
      accountId: info.accountId,
      route,
      mono: deps.mono,
      scope: viewScope,
      commandsEnabled,
      acknowledgements,
      streamSequence,
      capabilities,
      commands: commandServices,
    });
    setChildren(main, [view.element]);
    if (routed) {
      win.document.getElementById("main")?.focus();
    }
    routed = true;
  };

  async function switchAccount(accountId: string): Promise<void> {
    stream?.dispose();
    stream = null;
    client.newGeneration();
    clearCommands();
    clearDetailCache();
    streamSequence.set(null);
    change({
      accountId,
      connection: { kind: "idle" },
      freshness: null,
      lastSync: null,
      ageUs: null,
      entryBlocks: [],
      strategyVersion: null,
      exchangeDate: null,
    });
    mount(lastParsed);
    await activate();
  }

  async function activate(): Promise<void> {
    try {
      await loadContext();
      if (!signedOut) {
        startStream();
      }
    } catch (error) {
      setChildren(main, [renderStateBanner(stateOfError(error))]);
    }
  }

  const rum = createRum(support.family, async (path, payload) => {
    await client.post(path, payload, () => undefined);
  });

  /** Send the page-load timing when the server advertises the endpoint; a failure never disturbs the page. */
  const report = async (): Promise<void> => {
    rum.advertise(capabilities);
    try {
      await rum.flush(pageLoadMs(win, deps.mono));
    } catch {
      // Monitoring is best effort.
    }
  };

  const start = async (): Promise<void> => {
    try {
      change({ environment: (await client.session()).environment });
      try {
        capabilities = decodeProjectionHealth(
          await client.get(HEALTH_PATH, new URLSearchParams(), (json) => json),
        ).capabilities;
      } catch {
        capabilities = OFF;
      }
      const accounts = await client.read("accounts", decodeAccountRow);
      const rows: readonly AccountRow[] = accounts?.page.rows ?? [];
      const wanted = lastParsed.kind === "route" ? lastParsed.route.query.account : undefined;
      const chosen = rows.find((row) => row.accountId === wanted) ?? rows[0];
      change({ accounts: rows, accountId: chosen?.accountId ?? null });
      booted = true;
      mount(lastParsed);
      await activate();
      await report();
    } catch (error) {
      setChildren(main, [renderStateBanner(stateOfError(error))]);
    }
  };

  root.replaceChildren(
    h("a", { class: "skip-link", attrs: { href: "#main" } }, "Skip to content"),
    bar.element,
    nav,
    main,
    drawer.element,
  );
  bar.update(info);
  recompute();
  scope.add(() => {
    view?.dispose();
    stream?.dispose();
    commandsEnabled.dispose();
    streamSequence.dispose();
    keys.dispose();
  });
  listenForErrors(win, scope, rum);
  installPageHide(win, scope, () => {
    stream?.dispose();
    stream = null;
  });
  startRouter(win, scope, mount);
  const ready = start();
  return {
    ready,
    dispose: () => {
      scope.dispose();
    },
  };
}
